import { normalizePostalAreaCode } from '@/lib/postal-code';

/**
 * The one comparator for "does this billing address name the same place as the
 * service address?", shared by every surface that asks.
 *
 * NOT-586 chose NORMALIZED FIELD COMPARISON over a stored "same as service"
 * flag. A flag is the tidier model, but it is a schema + API change across
 * estimates, proposals and jobs, and it would still have to be *derived* for
 * every record written before the flag existed — i.e. by this comparison
 * anyway. Normalizing answers the question identically for old and new records,
 * so it is the whole answer rather than half of one.
 *
 * The false "Different from service address" warnings this replaces came from
 * six copies of `addressesEqual` that had drifted apart: some compared with
 * `===`, some trimmed and lower-cased, one compared rendered display text. The
 * same two addresses could be "the same" on the estimate and "different" on the
 * job.
 */

/**
 * Every address shape in the app, reconciled. Saved contact addresses and
 * estimate/job payloads use `street`/`zip`; catalog bill-to overrides and lead
 * drafts use `street1`/`postalCode`. Accepting both here is what lets one
 * comparator serve all of them without an adapter at each call site.
 */
export interface AddressPartsLike {
  addressId?: string | null;
  street?: string | null;
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

export interface AddressMatchOptions {
  /**
   * Compare `country` as well as the five place fields. Off by default — see
   * `normalizedMatch`. Turn it on only where both operands are user-supplied
   * form state, never where either side is a persisted snapshot.
   */
  compareCountry?: boolean;
}

interface NormalizedAddress {
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

/**
 * Casing and spacing are how a value was typed, not which place it names:
 * "827 W  Washington Blvd" and "827 w washington blvd" are one address.
 *
 * Internal whitespace collapses to a single space rather than to nothing, so a
 * difference in word or digit grouping survives — "12 34 Main St" must not
 * become "1234 Main St".
 */
function normalizeField(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Postal codes are the one field where internal space carries no meaning:
 * "V3M 0P2" and "V3M0P2" are the same Canadian code, and users type both.
 * Routed through the shared postal helpers so postal rules live in one place —
 * this module must not grow its own.
 *
 * `normalizePostalAreaCode` and not `normalizePostalCode`, because the ZIP+4
 * suffix names a delivery route within a ZIP, not a different place: "60607"
 * and "60607-2031" are one address once street, unit, city and state agree.
 * Which of the two a value carries is an artifact of the surface that produced
 * it — a hand-typed service row keeps five digits while an autocompleted or
 * imported billing value carries the +4 — so comparing them literally raised a
 * false "Different from service address", dropped a reopened record out of
 * `select` mode, and let a redundant address row be written for the same place.
 *
 * The two helpers differ ONLY on US ZIP+4; every other input, Canadian codes
 * included, falls through `normalizePostalAreaCode` untouched.
 */
function normalizeZip(value: string | null | undefined): string {
  return normalizePostalAreaCode(value ?? '').toLowerCase();
}

/**
 * `||` and not `??`: form state spells an absent street as `''`, so a draft
 * carrying both keys would fall through the nullish check with the empty one.
 */
function normalizeAddress(address: AddressPartsLike): NormalizedAddress {
  return {
    street: normalizeField(address.street || address.street1),
    street2: normalizeField(address.street2),
    city: normalizeField(address.city),
    state: normalizeField(address.state),
    zip: normalizeZip(address.zip || address.postalCode),
    country: normalizeField(address.country),
  };
}

/** A country on its own describes no place, so it does not count as content. */
function hasContent(address: NormalizedAddress): boolean {
  return Boolean(address.street || address.street2 || address.city || address.state || address.zip);
}

/**
 * Country is not compared by default, because half our operands carry a country
 * nobody chose: every persisted snapshot is stamped `country ?? 'US'` on the way
 * out, while the ContactAddress row it came from keeps the real value. A saved
 * Canadian address therefore reads 'CA' from the row and 'US' from its own
 * snapshot and stops matching itself — which drops a reopened estimate, job or
 * proposal from `select` mode to `manual`, losing the address-level tax-exemption
 * fields on the next save.
 *
 * The five remaining fields decide the place; two addresses sharing all of them
 * across a border is not a case our data can produce. This is what the
 * comparators this module replaced did, so it restores prior behaviour.
 *
 * `compareCountry` opts back in where BOTH operands are form state of the same
 * shape. There an absent country is a real absence rather than a stamp, so
 * defaulting both sides is safe and a country change is a genuine edit.
 */
function normalizedMatch(
  a: NormalizedAddress,
  b: NormalizedAddress,
  compareCountry = false,
): boolean {
  if (compareCountry && (a.country || 'us') !== (b.country || 'us')) return false;
  return (
    a.street === b.street &&
    a.street2 === b.street2 &&
    a.city === b.city &&
    a.state === b.state &&
    a.zip === b.zip
  );
}

/**
 * Whether two addresses name the same place.
 *
 * An absent or empty address on either side is `false` — not "they match", but
 * "there is nothing to compare". Callers that mean "same as service" must not
 * read a blank billing address as agreement.
 */
export function addressesMatch(
  left: AddressPartsLike | null | undefined,
  right: AddressPartsLike | null | undefined,
  options: AddressMatchOptions = {},
): boolean {
  if (!left || !right) return false;
  const a = normalizeAddress(left);
  const b = normalizeAddress(right);
  if (!hasContent(a) || !hasContent(b)) return false;
  return normalizedMatch(a, b, options.compareCountry);
}

/**
 * Whether two addresses name DIFFERENT places — the question a UI warning asks.
 *
 * Deliberately not `!addressesMatch`: both return `false` when either side is
 * empty, because a UI must not assert a difference it cannot see. "Billing
 * address not selected" is not evidence that billing differs from service.
 */
export function addressesDiffer(
  left: AddressPartsLike | null | undefined,
  right: AddressPartsLike | null | undefined,
  options: AddressMatchOptions = {},
): boolean {
  if (!left || !right) return false;
  const a = normalizeAddress(left);
  const b = normalizeAddress(right);
  if (!hasContent(a) || !hasContent(b)) return false;
  return !normalizedMatch(a, b, options.compareCountry);
}

/**
 * Canadian postal codes split across a space, joined back up — the text-level
 * counterpart of `normalizeZip`. Anchored on the letter-digit-letter /
 * digit-letter-digit shape so it cannot touch a house number.
 */
const SPLIT_CA_POSTAL_RE = /\b([a-z]\d[a-z]) (\d[a-z]\d)\b/g;

/**
 * US ZIP+4 reduced to its 5-digit prefix — the text-level counterpart of
 * `normalizeZip`'s `normalizePostalAreaCode`. Both comparators must answer
 * "60607" vs "60607-2031" the same way, or a rendered snapshot and a structured
 * address disagree about the same place.
 *
 * Bounded by whitespace or string edge rather than `\b`, so it can only fire on
 * a whole token. `\b` alone would also match inside a longer hyphenated run
 * such as "1-12345-6789" and collapse part of it.
 */
const US_ZIP4_RE = /(^|\s)(\d{5})-\d{4}(?=\s|$)/g;

/**
 * Separators collapse to a space rather than to nothing for the same reason
 * `normalizeField` keeps one: "12, 34 Main St" must not equal "1234 Main St".
 *
 * The postal rewrites run last, on already-collapsed whitespace, so both can
 * rely on single spaces and on the ZIP being a standalone token.
 */
function normalizeAddressText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[,;.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(SPLIT_CA_POSTAL_RE, '$1$2')
    .replace(US_ZIP4_RE, '$1$2');
}

/**
 * Compare two RENDERED address strings.
 *
 * Only for the places that hold a display snapshot and no structured address —
 * an estimate's saved `customer.address` is a formatted string, so there are no
 * fields to compare. Prefer `addressesMatch` wherever the structure survives;
 * this is the lossy fallback, not the default.
 */
export function addressTextMatches(left: string, right: string): boolean {
  return normalizeAddressText(left) === normalizeAddressText(right);
}
